import React, { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Query } from '@apollo/react-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  Acknowledgement,
  Certificate,
  ConformityCertificate,
  Customer,
  CustomerAttributeConfig,
  CustomerAuditByField,
  CustomerBudgetProfile,
  CustomerContact,
  CustomerContactAttributeConfig,
  CustomerNoteType,
  DataSheet,
  DirectDebitRequestLine,
  DispatchInstructions,
  OverdueAccount,
  Payment,
  ProformaInvoice,
  QuotationDocument,
  ReportTemplate,
  SalesAttributeConfig,
  SalesCredit,
  SalesInvoice,
  SystemPrinter,
} from '../../graphql/entities';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import GridContainer from '../../components/connectedForm/GridContainer';
import CombinedSalesWorkspace from '../sales/CombinedSalesWorkspace';
import DiaryEventWorkspace from '../diary/DiaryEventWorkspace';
import getIn from '../../components/connectedForm/utils/getIn';
import { createDropdownOptions, getNominalConfig } from '../../utils/DimensionSets';
import { normalizeUpperCase } from '../../utils/Values';
import ColumnLabel from '../../components/connectedForm/ColumnLabel';
import AuditByFieldWorkspace from '../company/AuditByFieldWorkspace';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import componentQuery from '../../utils/componentQuery';
import ContactTab, { IContactTabProps } from './ContactTab';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import SalesInvoiceAnalysis, {
  EmbeddedSalesInvoiceOptions,
} from '../salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysis';
import SalesTransactionEnquiryForm from '../salesLedger/SalesTransactionEnquiryForm';
import EmbeddedFormContainer from '../../components/connectedForm/EmbeddedFormContainer';
import BudgetTab from '../nominal/BudgetTab';
import DeliveryInstructionsTab from '../../views/customer/DeliveryInstructionsTab';
import DirectDebitTab from './DirectDebitTab';
import CustomerProductHistoryWorkspace from './CustomerProductHistoryWorkspace';
import FieldMetaData, { IEnumMetaData } from '../../graphql/schema/fieldMetaData';
import productConfigQuery from 'graphql/queries/product/ProductConfigQuery.graphql';
import AbstractDocumentConfig from '../../views/AbstractDocumentConfig';
import DropdownEditor from '../../components/DropdownEditor/DropdownEditor';
import productDropdown, { GridExtendedEntityType } from '../../utils/GridDropdownSetup';
import { getEntryCount } from '../../utils/CountUtil';
import SalesOrderLineWorkspace from '../sales/SalesOrderLineWorkspace';
import CustomerOverviewTab from './CustomerOverviewTab';
import ContactCard from './ContactCard';
import CustomerPriceListWorkspace from '../../views/customer/CustomerPriceListWorkspace';
import AppContext from '../../components/Context/AppContext';
import CurrencyContext from '../../components/Context/CurrencyContext';
import { useCompany, useCustomerConfig, useSalesOrderConfig } from '../../utils/FetchConfig';

import DocumentSummaryWorkspace from '../company/DocumentSummaryWorkspace';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { ConnectedFormPristinePrompt } from '../../components/connectedForm/ConnectedFormPristinePrompt';
import {
  CashStatus,
  FilterOperation,
  ICompanyFragment,
  ICompanyUser,
  ICurrency,
  ICustomer,
  ICustomerConfigFragment,
  ICustomerNotesQueryResult,
  ICustomerNotesQueryVariables,
  IFilterInput,
  IOrderConfig,
  ISalesOrder,
  ISalesOrderConfigFragment,
  Maybe,
  ReportType,
} from '../../graphql/schema/p4types';
import { Dispatch } from 'redux';
import { EntityType } from '../../@types/entityTypes';
import { FormAlertDetails, FormAlertRow } from '../common/FormAlertsTab';
import { IGenericArrayProps } from '../../@types/genericArray';
import { IDropdownProps } from '../../@types/dropdown';
import {
  BlurTextFieldType,
  HrefLink,
  IBlurTextFieldProps,
} from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IValueFieldProps } from '../../components/connectedForm/ValueField';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { IEditableDataGridProps } from '../../components/EditableDataGrid/EditableDataGrid';

import { DocumentNode } from 'graphql';
import { useHistory } from 'react-router';

import { useCustomerOverdueBalance } from './useCustomerOverdueBalance';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import useEntityCacheUpdate from '../../graphql/hooks/useEntityCacheUpdate';

import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../../components/connectedForm/MemoEditor/MemoEditor';
import CustomerNotesWorkspaceQuery from '../../graphql/queries/customer/CustomerNotesWorkspaceQuery.graphql';
import OutlookTab from '../common/OutlookTab';
import AutomatedCreditControlResultWorkspace from '../salesLedger/AutomatedCreditControlResultWorkspace';
import { cloneDeep } from '@apollo/client/utilities';
import { useCustomerFormActions } from '../../actions/customer/CustomerAction';
import { getEmailAddresses } from '../../utils/getContactEmailAddresses';
import { Paths } from '../../@types/keyOfNested';
import { ConnectedAddress } from '../../components/connectedForm/ConnectedAddress';
import { ICheckboxInputProps } from '../../components/connectedForm/CheckboxInput/CheckboxInput';
import Formatters from '../../components/grid-addons/formatters';
import CheckboxInputEditor from '../../components/CheckboxInputEditor/CheckboxInputEditor';
import { IGridUploaderProps } from '../../components/connectedForm/GridUploader';
import useInstrastatHints from '../../utils/useInstrastatHints';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';

const AnyCheckboxEditor = CheckboxInputEditor as any;

const {
  default: defaultQuery,
  actions,
  formTitle,
  dropdown,
  workspaceUrl,
  temporary,
  update,
  instance,
  fragment,
  temporaryUpdate,
  type,
} = Customer;

type ICustomerFormProps = {
  overrideParams: any;
  values: ICustomer;
  dispatch: Dispatch<any>;
  change: (
    //Not 100% about this - Not certain what 'bindActionCreators' is doing with the types in connectedForm.js.
    field: false | string,
    value: any
  ) => { type: string; meta: { form: string; field: string; touch: boolean; mutationId: string }; payload: any };
  readOnly: boolean;
  initialized: boolean;
  customerConfig: Maybe<{ __typename?: 'CustomerConfig' } & ICustomerConfigFragment>; //Fetched by 'useCustomerConfig'
  salesOrderConfig: Maybe<{ __typename?: 'OrderConfig' } & ISalesOrderConfigFragment>; //Fetched by 'useSalesOrderConfig'
  company: Maybe<{ __typename?: 'Company' } & ICompanyFragment>; //Fetched by 'useCompany'
  match: {
    params: any;
  };
  companyUser?: ICompanyUser;
  detailView?: ReactElement;
  activeTabId?: string;
  history: History;
  pageView?: boolean;
};

const templateFilter: IFilterInput = {
  filterOperation: FilterOperation.Equals,
  filterParameters: {
    field: 'template',
    value: 'true',
  },
};

const CUSTOMER_STATUS_FLAGS = [
  {
    key: 'customerAccountStatus.onStop',
    status: [{ value: true, style: 'error', title: 'ON STOP' }],
  },
  {
    key: 'foreignCurrency',
    status: [
      {
        value: true,
        style: 'info',
        icon: 'account_balance',
        title: '',
        titleKey: 'currency.isoCode.codeAlpha3',
      },
    ],
  },
  {
    key: 'defaultCashStatus',
    status: [
      {
        value: 'PRO_FORMA',
        style: 'info',
        title: 'Pro forma',
      },
    ],
  },
  {
    key: 'defaultVatInclusive',
    status: [
      {
        value: true,
        style: 'info',
        title: 'Vat Inclusive',
      },
    ],
  },
];

const useCustomerFilters = (id) => {
  const customerIdFilter = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'customer.id',
        value: id,
      },
    },
  ];

  return {
    transactionPredefinedFilters: customerIdFilter,
    eventPredefinedFilters: customerIdFilter,
    customerIdFilter: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'id',
          value: id,
        },
      },
    ],
    orderLineIdFilter: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'salesOrder.customer.id',
          value: id,
        },
      },
    ],
    predefinedAnalysisFilters: [
      {
        dimension: 'CUSTOMER',
        id: id,
      },
    ],
  };
};

/**
 * Render Address
 * @param cols - Column width (Passed through to the returned fieldset).
 * @param newAccount - Is this a new record?
 * @returns - A fieldset containing the address information.
 */
const CustomerAddressInfo = ({ cols, newAccount }: { cols: number; newAccount: boolean }) => {
  return (
    <Form.Fieldset cols={cols}>
      <Form.Legend label="Account/Address Details" />
      <Form.Field<IBlurTextFieldProps>
        dataid="reference"
        component={input.BlurTextField}
        label="Reference"
        normalize={normalizeUpperCase}
        spellCheck="false"
      />
      <Form.Field<IBlurTextFieldProps>
        dataid="oldReference"
        component={input.BlurTextField}
        label="Old Reference"
        normalize={normalizeUpperCase}
        spellCheck="false"
        readOnly={true}
        hint={'Imported customer reference from previous/other system.'}
        hideWhenEmpty={true}
      />
      <Form.Field dataid="name" component={input.BlurTextField} label="Name" autoFocus={newAccount} />
      <ConnectedAddress dataid={'address'} loqateProps={{ companyDataid: 'name' }} />
      <Form.Legend label="Account Activity" />
      <Form.Field dataid="createdBy" component={input.BlurTextField} label="Created By" readOnly={true} />
      <Form.Field<IDateFieldProps>
        dataid="createdAt"
        component={input.DateField}
        label="Date/Time Created"
        dateFormat="iii do MMM yyyy HH:mm"
        readOnly={true}
      />
      <Form.Field dataid="updatedBy" component={input.BlurTextField} label="Last Updated By" readOnly={true} />
      <Form.Field<IDateFieldProps>
        dataid="updatedAt"
        component={input.DateField}
        label="Date/Time Updated"
        dateFormat="iii do MMM yyyy HH:mm"
        readOnly={true}
      />
      <Form.Field<IDateFieldProps>
        dataid="lastContactDate"
        component={input.DateField}
        label="Last Contact"
        dateFormat="iii do MMM yyyy"
        readOnly={true}
      />
    </Form.Fieldset>
  );
};

const MAIL_ITEM_FROM_QUERY_PARAM = 'mailItemFrom';
const MAIL_ITEM_FROM_NAME_QUERY_PARAM = 'mailItemFromName';

const CUSTOMER_QUOTES_AND_ORDERS_FIELD_NAMES: Array<Paths<ISalesOrder>> = [
  'orderNumber',
  'createdAt',
  'orderReference',
  'headerTotal',
  'orderType',
  'salesOrderStatus',
];

// eslint-disable-next-line complexity
const CustomerForm = (props: ICustomerFormProps) => {
  const [dimension1, setDimension1] = useState<ReactElement | null>(null);
  const [dimension2, setDimension2] = useState<ReactElement | null>(null);
  const [dimension3, setDimension3] = useState<ReactElement | null>(null);

  const dispatch = useDispatch();
  const { user, fullUserDetails } = useContext(AppContext);

  const company = useCompany();
  const salesOrderConfig = useSalesOrderConfig();
  const { baseValues } = useContext(ConnectedFormContext);

  const [actionButtons, actionModals] = useCustomerFormActions(props.pageView);

  const id = getIn(props, 'match.params.id') || props.overrideParams?.id;
  const newAccount = !(id && id !== 'New');

  const {
    transactionPredefinedFilters,
    eventPredefinedFilters,
    customerIdFilter,
    orderLineIdFilter,
    predefinedAnalysisFilters,
  } = useCustomerFilters(newAccount ? null : id);

  const { overdueBalance } = useCustomerOverdueBalance(props);

  const tabPanelRef = useRef();

  const memoEditorProps = useMemoEditorProps<ICustomerNotesQueryResult, ICustomerNotesQueryVariables>(
    CustomerNotesWorkspaceQuery,
    IMemoEditorType.Customer,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'customer.id',
            value: props.values.id,
          },
        },
      ],
    }
  );

  useEffect(() => {
    const response = getNominalConfig();
    response.then((config) => {
      const { dimension1, dimension2, dimension3 } = config;
      const dim1 = dimension1 ? renderDimension(1, dimension1) : null;
      const dim2 = dimension2 ? renderDimension(2, dimension2) : null;
      const dim3 = dimension3 ? renderDimension(3, dimension3) : null;
      setDimension1(dim1);
      setDimension2(dim2);
      setDimension3(dim3);
    });
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has(MAIL_ITEM_FROM_QUERY_PARAM)) {
      dispatch(props.change('email', queryParams.get(MAIL_ITEM_FROM_QUERY_PARAM)));
    }

    if (queryParams.has(MAIL_ITEM_FROM_NAME_QUERY_PARAM)) {
      dispatch(props.change('name', queryParams.get(MAIL_ITEM_FROM_NAME_QUERY_PARAM)));
    }
  }, [window.location.search, props.values.email === undefined, props.values.name === undefined]);

  const emailAddresses = useMemo(() => {
    return getEmailAddresses(props.values);
  }, [props.values.email, props.values.contacts]);

  const renderDimension = (idx, dimension) => {
    const config = createDropdownOptions(dimension);
    return (
      <Form.Field<IDropdownProps>
        dataid={`dimensionValueEmbedded.dimensionReference${idx}`}
        component={input.Dropdown}
        label={dimension.title}
        options={config.optionList}
        labelKey={config.labelKey}
        valueKey={config.valueKey}
        labelCols={5 / 12}
        inputCols={7 / 12}
      />
    );
  };

  const renderContactRow = (rowData, rowIndex) => {
    return <Form.Field dataid={`contacts.${rowIndex}`} component={ContactCard} />;
  };

  const renderContactDetails = (rowData, rowIndex) => {
    const { readOnly, values, change, initialized } = props;

    return !initialized ? null : (
      <Form.Field<IContactTabProps & { entity: EntityType }>
        dataid={`contacts.${rowIndex}`}
        component={ContactTab}
        index={rowIndex}
        readOnly={readOnly}
        values={values}
        change={change}
        type={'customer'}
        attributeConfig={CustomerContactAttributeConfig}
        temporary={true}
        forceUpdate={true}
        entity={CustomerContact}
      />
    );
  };

  const renderOverallDiscountHeader = () => {
    return (
      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <ColumnLabel label="Threshold" />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 2}>
          <ColumnLabel label="Discount" />
        </Form.Fieldset>
      </Form.Row>
    );
  };

  const renderOverallDiscount = (rowData, rowIndex) => {
    return rowData.deleted ? null : (
      <Form.Row>
        <Form.Fieldset cols={1 / 2} collapsed={true}>
          <Form.Field<IBlurTextFieldProps>
            dataid={`customerDiscounts.${rowIndex}.threshold`}
            component={input.BlurTextField}
            includeLabel={false}
            temporary={true}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 2} collapsed={true}>
          <Form.Field<IBlurTextFieldProps>
            dataid={`customerDiscounts.${rowIndex}.overallDiscountPercent`}
            component={input.BlurTextField}
            includeLabel={false}
            temporary={true}
          />
        </Form.Fieldset>
      </Form.Row>
    );
  };

  const getDocumentDefaultList = () => {
    const configs = [
      Acknowledgement.configuration,
      QuotationDocument.configuration,
      DispatchInstructions.configuration,
      {
        title: 'Statements',
        field: 'statementDocumentDefault',
        reportType: ReportType.CustomerStatementReport,
        customerField: 'customerStatementDocumentDefault',
        receiveFlag: 'receiveStatementDocuments',
      },
      ConformityCertificate.configuration,
      SalesInvoice.configuration,
      ProformaInvoice.configuration,
      DataSheet.configuration,
      {
        title: 'Dispatch Email',
        field: 'dispatchEmailDefault',
        reportType: ReportType.DispatchEmail,
        customerField: 'customerDispatchEmailDefault',
        receiveFlag: 'receiveDispatchEmails',
      },
      Payment.configuration,
      SalesCredit.configuration,
      OverdueAccount.configuration,
      DirectDebitRequestLine.configuration,
    ];

    return configs.map((configuration) => {
      return {
        ...configuration,
        config: getIn(props.values, configuration?.customerField),
      };
    });
  };

  const renderDocumentDetails = (rowData) => {
    if (!rowData?.customerField) return null;
    const field = rowData.customerField;

    const config = rowData.config;

    if (!config) return null;

    const reportTypeFilters = [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'reportType',
          value: rowData.reportType,
        },
      },
    ];

    const documentRequired = (
      <Form.Field label="Required" dataid={`${field}.documentRequired`} component={input.Checkbox} />
    );
    let invoiceFields, output, outputConfig, statementFields;

    if (field !== 'customerDispatchEmailDefault' && field !== 'customerDataSheetDocumentDefault') {
      output = (
        <Form.Field<IDropdownProps>
          label="Output"
          dataid={`${field}.output`}
          component={input.Dropdown}
          options={(FieldMetaData.DocumentOutputType as IEnumMetaData).enumValues}
          enum={true}
        />
      );
    }
    const layout = (
      <Form.Field<IDropdownProps>
        label="Layout"
        dataid={`${field}.layout`}
        component={input.Dropdown}
        {...ReportTemplate.dropdown}
        predefinedFilters={reportTypeFilters}
      />
    );
    if (field === 'customerStatementDocumentDefault') {
      statementFields = (
        <>
          <Form.Field<IDropdownProps>
            dataid="customerStatementDocumentDefault.statementTransactions"
            component={input.Dropdown}
            label="Transactions"
          />
          {/* Do not remove; see Jira ticket P4-6842 */}
          {/*<Form.Field*/}
          {/*  dataid={'customerStatementDocumentDefault.statementConsolidated'}*/}
          {/*  component={input.Checkbox}*/}
          {/*  label="Consolidated"*/}
          {/*/>*/}
        </>
      );
    } else if (field === 'customerInvoiceDocumentDefault') {
      invoiceFields = (
        <Form.Field<IDropdownProps>
          dataid={'customerInvoiceDocumentDefault.invoiceConsolidationOption'}
          component={input.Dropdown}
          label="Consolidation"
          hint="When consolidation by warehouse is enabled, only orders with the same warehouse & payment terms will be consolidated"
        />
      );
    }
    if (config.output === 'SYSTEM_PRINTER') {
      outputConfig = (
        <Form.Field<IDropdownProps>
          label="System Printer"
          dataid={`${field}.systemPrinterId`}
          component={input.Dropdown}
          valueOnly
          {...SystemPrinter.dropdown}
        />
      );
    }

    const customerContacts = getIn(props, 'values.contacts');
    const documentContacts = customerContacts
      ? customerContacts.filter((customerContact) => {
          return customerContact[rowData.receiveFlag];
        })
      : [];

    const id = getIn(props, 'match.params.id') || props.overrideParams?.id;

    const recipients =
      id !== 'New' ? (
        <QueryDropdown
          data-testid={'customer-contact-recipients'}
          label={'Recipients'}
          {...CustomerContact.dropdown}
          multi={true}
          predefinedFilters={transactionPredefinedFilters}
          value={documentContacts}
          onChange={(values) => {
            let customerContacts = cloneDeep(getIn(props, 'values.contacts'));
            if (customerContacts === null) return;

            customerContacts = customerContacts.map((contact) => {
              contact[rowData.receiveFlag] =
                values !== null && values.findIndex((value) => !value.deleted && value.id === contact.id) >= 0;
              return contact;
            });
            dispatch(props.change('contacts', customerContacts));
          }}
        />
      ) : null;

    return (
      <Form.Fieldset cols={1}>
        <Form.Legend label={rowData.title} />
        {documentRequired}
        {output}
        {layout}
        {outputConfig}
        {statementFields}
        {invoiceFields}
        {recipients}
      </Form.Fieldset>
    );
  };

  const templateToForm = (value) => {
    const vars = { id: value.id };
    componentQuery(defaultQuery, vars, true).then(({ response: data }) => {
      dispatch(props.change(false, data.defaultRecord));
    });
  };

  const renderDefaultPriceBand = () => {
    return (
      <Query query={productConfigQuery}>
        {({ data }) => {
          if (!data || !data.data) {
            return null;
          }

          const options = [
            {
              value: 'SALES_PRICE_MINIMUM',
              title: 'Sales Price Minimum',
            },
          ];

          for (let i = 1; i <= 10; i++) {
            const entry = data.data[`sellingPriceTitle${i}`];

            if (!entry) continue;
            options.push({
              value: `SALES_PRICE_${i}`,
              title: entry,
            });
          }

          return (
            <Form.Field<IDropdownProps>
              dataid="defaultPriceBand"
              component={input.Dropdown}
              label="Price Band"
              clearable={false}
              options={options}
              enumValues={true}
              valueOnly={true}
              valueKey={'value'}
              labelKey={'title'}
            />
          );
        }}
      </Query>
    );
  };

  const defineColumnMeta = () => {
    const productOptions: GridExtendedEntityType = productDropdown(salesOrderConfig as IOrderConfig);

    productOptions.dropdown!.clearable = false;
    productOptions.dropdown!.selectModal = 'SalesProductLookup';
    productOptions.dropdown!.selectModalData = () => {
      return { customer: props.values };
    };

    return [
      {
        name: 'Product',
        key: 'product',
        editable: true,
        editor: DropdownEditor,
        options: productOptions,
        formatter: 'dropdown',
        width: 200,
        resizable: false,
      },
      {
        name: 'Part Number',
        key: 'partNumber',
        editable: true,
        resizable: true,
      },
      {
        name: 'Description',
        key: 'description',
        editable: true,
        resizable: true,
      },
      {
        name: 'Certificate',
        key: 'certificate',
        editor: <DropdownEditor />,
        options: Certificate,
        editable: true,
        width: 200,
        resizable: true,
        formatter: 'dropdown',
      },
    ];
  };

  const deleteCustomerPartNumber = (rowIdx) => {
    const dataid = `customerPartNumbers.${rowIdx}.deleted`;
    dispatch(props.change(dataid, !getIn(props.values, dataid)));
  };

  const isNotNew = (props.match?.params?.id || props.overrideParams?.id) !== 'New';
  const { values: customerRecord } = props;

  const current = getIn(company, 'currentFinancialPeriod') ? getIn(company, 'currentFinancialPeriod.name') : 'Current';
  const period1 = getIn(company, 'month1FinancialPeriod') ? getIn(company, 'month1FinancialPeriod.name') : 'Month +1';
  const period2 = getIn(company, 'month2FinancialPeriod') ? getIn(company, 'month2FinancialPeriod.name') : 'Month +2';
  const period3 = getIn(company, 'month3FinancialPeriod') ? getIn(company, 'month3FinancialPeriod.name') : 'Month +3';
  const period4 = getIn(company, 'month4FinancialPeriod') ? getIn(company, 'month4FinancialPeriod.name') : 'Month +4';

  const allowPhoneCalls = getIn(customerRecord, 'allowPhoneCalls') ?? false;
  const allowFax = getIn(customerRecord, 'allowFax');

  const companyUser = props.companyUser;

  const currentYear = getIn(company, 'currentFinancialYear');
  const attachmentsCount = getEntryCount(props.values.attachments);

  const isCashAccount = customerRecord?.defaultCashStatus === CashStatus.CashWithOrder;

  const customerId = props.overrideParams?.id || props.match?.params?.id;

  const noDeleteOption = props.values.balance
    ? (props.values.balance?.balance !== 0 && props.values.balance?.balance !== null) ||
      (props.values.outstandingOrderValue !== 0 && props.values.outstandingOrderValue !== null) ||
      (props.values.outstandingQuoteValue !== 0 && props.values.outstandingQuoteValue !== null) ||
      overdueBalance?.overdueAmount !== 0
    : false;

  const availableColumnsFilter = (column): boolean => {
    return (
      column.fieldName !== 'customer.reference' &&
      column.fieldName !== 'customer.name' &&
      column.fieldName !== 'customer.email' &&
      column.fieldName !== 'customer.telephoneNumber' &&
      column.fieldName !== 'customer.address.country.description' &&
      column.fieldName !== 'customer.customerAcknowledgementDefault.output' &&
      column.fieldName !== 'customer.customerQuotationDocumentDefault.output'
    );
  };

  const availableFiltersFilter = (filter): boolean => {
    return (
      filter.fieldName !== 'customer.reference' &&
      filter.fieldName !== 'customer.name' &&
      filter.fieldName !== 'customer.email' &&
      filter.fieldName !== 'customer' &&
      filter.fieldName !== 'customer.telephoneNumber' &&
      filter.fieldName !== 'customer.address.country.description' &&
      filter.fieldName !== 'customer.customerAcknowledgementDefault.output' &&
      filter.fieldName !== 'customer.customerQuotationDocumentDefault.output'
    );
  };

  const showUkims = useMemo(() => {
    const windsorFrameworkEnabled = company?.windsorFrameworkEnabled;
    const address = props.values.address;
    const customerPostcode = address?.postCode?.toUpperCase() ?? '';
    const codeAlpha3 = address?.country?.isoCountry?.codeAlpha3 ?? '';

    return windsorFrameworkEnabled && codeAlpha3 === 'GBR' && customerPostcode.startsWith('BT');
  }, [company?.windsorFrameworkEnabled, props.values.address]);
  const { modeOfTransportHint, natureOfTransactionHint, customsProcedureCodeHint } = useInstrastatHints();

  return (
    <>
      <ConnectedFormPristinePrompt />
      {actionModals}
      <CurrencyContext.Provider value={{ currency: props.values?.currency || (company?.baseCurrency as ICurrency) }}>
        <Form.FormContainer>
          <Form.Header
            entityHdr="name"
            entityHdr2="reference"
            entityHdrStyle="small"
            statusFlags={CUSTOMER_STATUS_FLAGS}
            actionButtons={actionButtons}
            component={Form.HeaderBlock}
            archive={true}
            cancelText={props.detailView ? 'Close' : 'Cancel'}
            detailView={props.detailView}
            noDeleteOption={noDeleteOption}
            allowRestoreOption={true}
          />
          <Form.Form>
            <Form.TabPanel ref={tabPanelRef} active={props.activeTabId || (!newAccount ? 'overview' : 'main')}>
              <Form.TabContent id="overview" label="Overview" shouldRender={!newAccount && props.values.id}>
                <CustomerOverviewTab
                  id={props.values.id}
                  values={props.values}
                  sidebar={false}
                  tabPanelRef={tabPanelRef}
                  overdueBalance={overdueBalance}
                />
              </Form.TabContent>
              <Form.TabContent id="main" label="General">
                <Form.Row>
                  {props.values.id !== undefined && props.values.id === null ? (
                    <Form.Fieldset cols={1}>
                      <Form.Legend label="Template" />
                      <QueryDropdown
                        data-testid={'customer-main-template'}
                        value=""
                        label="Template"
                        customFilter={() => [templateFilter]}
                        onChange={templateToForm}
                        {...dropdown}
                        placeholder=""
                        selectModal={null}
                        preLoad={true}
                      />
                      <Form.Field dataid="template" component={input.Checkbox} label="Save as template" />
                    </Form.Fieldset>
                  ) : null}
                  <CustomerAddressInfo cols={1 / 2} newAccount={newAccount} />
                  <Form.Fieldset cols={1 / 2}>
                    <Form.Legend label="Contact Details" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="email"
                      component={input.BlurTextField}
                      label="Email"
                      hyperlink={HrefLink.MailTo}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="telephoneNumber"
                      component={input.BlurTextField}
                      label="Telephone Numbers"
                      type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                      hint={allowPhoneCalls ? undefined : 'DO NOT CONTACT'}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="telephoneNumber2"
                      component={input.BlurTextField}
                      label=""
                      type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="mobileNumber"
                      component={input.BlurTextField}
                      label="Mobile Number"
                      type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                      hint={allowPhoneCalls ? undefined : 'DO NOT CONTACT'}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="faxNumber"
                      component={input.BlurTextField}
                      label="Fax Numbers"
                      type={allowFax ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                      hint={allowFax ? undefined : 'DO NOT CONTACT'}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="faxNumber2"
                      component={input.BlurTextField}
                      label=""
                      type={allowFax ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
                      hyperlink={HrefLink.Tel}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="website"
                      component={input.BlurTextField}
                      label="Website"
                      hyperlink
                    />
                    <Form.Legend label="Marketing" />
                    <Form.Field dataid="allowPhoneCalls" component={input.Checkbox} label="Allow Phone Calls" />
                    <Form.Field dataid="allowFax" component={input.Checkbox} label="Allow Fax" />
                    <Form.Legend label="Account Management" />
                    <Form.Field
                      dataid="customerAccountManagementGroup"
                      component={input.Dropdown}
                      label="Management Group"
                      readOnly={!companyUser || !!companyUser.ownCustomerOnly}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="outlook"
                label="Outlook"
                shouldRender={
                  isNotNew &&
                  emailAddresses.length > 0 &&
                  fullUserDetails?.flags?.outlookIntegrationEnabled &&
                  user?.msftAccessTokenSet
                }
              >
                <OutlookTab emailAddresses={emailAddresses} />
              </Form.TabContent>
              <Form.TabContent id="contacts" label="Contacts" permissionReference={'CUSTOMER_CONTACT_VIEW'}>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Field<IGenericArrayProps>
                      dataid="contacts"
                      component={input.GenericArray}
                      label="Contacts"
                      renderRow={renderContactRow}
                      renderDetails={renderContactDetails}
                      enableNewRows={true}
                      forceUpdate={true}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="events"
                label="Tasks"
                key="events"
                shouldRender={isNotNew}
                permissionReference={'TASK_VIEW'}
              >
                <GridContainer>
                  <DiaryEventWorkspace
                    embeddedView={true}
                    pageView={props.pageView}
                    predefinedFilters={eventPredefinedFilters}
                    viewScreenName="/Diary/Task/Modal"
                    removeAddButton={true}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="notesTab"
                label="Notes"
                badge={memoEditorProps.queryProps.data?.listData?.count}
                type="info"
                onlyRenderWhenActive={true}
                shouldRender={!newAccount}
              >
                <MemoEditor noteTypeEntity={CustomerNoteType} {...memoEditorProps} />
              </Form.TabContent>
              <Form.TabContent
                id="sales"
                label="Quotes & Orders"
                key="sales"
                shouldRender={isNotNew}
                permissionReference={'SALES_ORDERS_VIEW'}
              >
                <GridContainer>
                  <CombinedSalesWorkspace
                    embeddedView={true}
                    additionalVariables={{ excludeCustomer: true }}
                    predefinedFilters={transactionPredefinedFilters}
                    viewScreenName="/sales/CombinedSales/Modal"
                    removeAddButton={true}
                    editInNewTab={false}
                    contextKey={'/sales/CombinedSales/Modal'}
                    availableFiltersFilter={availableFiltersFilter}
                    availableColumnsFilter={availableColumnsFilter}
                    fieldNameOverrides={CUSTOMER_QUOTES_AND_ORDERS_FIELD_NAMES}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="sales_order_lines"
                label="Sales Order Lines"
                key="sales_order_lines"
                shouldRender={isNotNew}
                permissionReference={'SALES_ORDERS_VIEW'}
              >
                <GridContainer>
                  <SalesOrderLineWorkspace
                    embeddedView={true}
                    pageView={props.pageView}
                    additionalVariables={{ excludeCustomer: true }}
                    predefinedFilters={orderLineIdFilter}
                    viewScreenName="/sales/OrderLines"
                    removeAddButton={true}
                    editInNewTab={false}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="analysis"
                label="Analytics"
                key="analysis"
                onlyRenderWhenActive={true}
                shouldRender={isNotNew}
                permissionReference={'SALES_LEDGER_ANALYTICS'}
              >
                <SalesInvoiceAnalysis
                  predefinedFilters={predefinedAnalysisFilters}
                  embedded={EmbeddedSalesInvoiceOptions.Customer}
                />
              </Form.TabContent>
              <Form.TabContent id="credit" label="Credit Control" permissionReference={'CUSTOMER_CREDIT_CONTROL_VIEW'}>
                <Form.Row>
                  <Form.Fieldset cols={4 / 8}>
                    <Form.Legend label="Credit/Payment Terms" />
                    <Form.Field
                      dataid="paymentTerm"
                      component={input.Dropdown}
                      label="Terms"
                      rawLabel={true}
                      readOnly={!companyUser?.canAmendCreditLimitAndPaymentTerms}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="creditLimit"
                      component={input.BlurTextField}
                      label="Credit Limit"
                      decimals={2}
                      type={BlurTextFieldType.Currency}
                      readOnly={!companyUser?.canAmendCreditLimitAndPaymentTerms}
                    />
                    <Form.Field dataid="creditAgency" component={input.Dropdown} label="Credit Agency" />
                    <Form.Field
                      dataid="creditAgencyReference"
                      component={input.BlurTextField}
                      label="Agency Reference"
                    />
                    <Form.Field dataid="dunsNumber" component={input.BlurTextField} label="DUNS Number" />
                    <Form.Field dataid="creditReviewDate" component={input.DateField} label="Last Review Date" />
                    <Form.Legend label="Account Status" />
                    <Form.Field
                      dataid="customerAccountStatus"
                      component={input.Dropdown}
                      label="Account Status"
                      readOnly={
                        (baseValues?.customerAccountStatus?.onStop ?? true) && !companyUser?.canRemoveCustomerFromStop
                      }
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="lastTransactionDate"
                      component={input.DateField}
                      label="Last Transaction"
                      dateFormat="iii do MMM yyyy"
                      readOnly={true}
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="lastOverdueAccountChaseLetter"
                      component={input.DateField}
                      label="Last Overdue Letter"
                      dateFormat="iii do MMM yyyy HH:mm"
                      readOnly={true}
                    />
                    <Form.Legend label="Sales/Invoicing" />
                    <Form.Field dataid="headOffice" component={input.Dropdown} label="Head Office" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="minimumOrderValue"
                      component={input.BlurTextField}
                      label="Minimum Order Value"
                      decimals={2}
                      type={BlurTextFieldType.Currency}
                    />
                    <Form.Field
                      dataid="defaultCashStatus"
                      component={input.Dropdown}
                      label="Cash with Order/Pro forma"
                      temporary={true}
                    />
                    <Form.Field dataid="invoiceFrequency" component={input.Dropdown} label="Invoice Frequency" />
                    <Form.Field
                      dataid="automatedCreditControlWorkflow"
                      component={input.Dropdown}
                      label="Automated credit control workflow"
                      readOnly={isCashAccount}
                    />
                    <Form.Field
                      dataid="excludeFromAutomatedCreditControl"
                      component={input.Checkbox}
                      label="Exclude from automated credit control communication?"
                      readOnly={isCashAccount}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={4 / 8}>
                    <Form.Legend label="Account Balances" />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.balance"
                      component={input.ValueField}
                      reformatLabel={false}
                      label="Total Balance"
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.future"
                      component={input.ValueField}
                      reformatLabel={false}
                      label="Future"
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.current"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={current}
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month1"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period1}
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month2"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period2}
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month3"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period3}
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                    <Form.Field<IValueFieldProps>
                      dataid="balance.month4"
                      component={input.ValueField}
                      reformatLabel={false}
                      label={period4}
                      decimals={2}
                      labelCols={6 / 12}
                      inputCols={6 / 12}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="automaticCreditControl"
                label="Automated Credit Control"
                permissionReference={'CUSTOMER_CREDIT_CONTROL_VIEW'}
              >
                <GridContainer>
                  <AutomatedCreditControlResultWorkspace
                    embeddedView={true}
                    pageView={props.pageView}
                    predefinedFilters={transactionPredefinedFilters}
                    viewScreenName="/customer/AutomatedCreditControlResult/Modal"
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="directDebitDetails"
                label="Direct Debit Details"
                key="directDebitDetails"
                tabStyle={{ width: 140 }}
                shouldRender={fullUserDetails?.flags?.directDebitCustomerPaymentsEnabled}
              >
                <DirectDebitTab />
              </Form.TabContent>
              <Form.TabContent
                id="transactions"
                label="Transactions"
                key="transactions"
                shouldRender={isNotNew}
                permissionReference={'SALES_LEDGER_TRANSACTIONS_VIEW'}
              >
                <EmbeddedFormContainer>
                  {props.values.id ? (
                    <SalesTransactionEnquiryForm
                      embedded={true}
                      customer={{ id: props.values.id, currency: props.values.currency }}
                      tourVisible={true}
                    />
                  ) : null}
                </EmbeddedFormContainer>
              </Form.TabContent>
              <Form.TabContent id="attributes" label="Attributes" permissionReference={'CUSTOMER_ATTRIBUTES_VIEW'}>
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="Attributes" />
                    <Form.Field<IAttributeArrayProps>
                      dataid="attributeValues"
                      component={input.AttributeArray}
                      label="Attributes"
                      query={CustomerAttributeConfig.list}
                      type={'customer'}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="vat" label="VAT/Nominal">
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="VAT" />
                    <Form.Field<IDropdownProps>
                      dataid="vatBusinessCategory"
                      component={input.Dropdown}
                      label="Business VAT Status"
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                      reformatLabel={false}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="vatRegistrationNumber"
                      component={input.BlurTextField}
                      label="Registration Number"
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="Nominal" />
                    <Form.Field<IDropdownProps>
                      dataid="nominalBusinessCategory"
                      component={input.Dropdown}
                      label="Nominal Business Category"
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                    />
                    <Form.Field<IDropdownProps>
                      dataid="nominalCustomerAccountSet"
                      component={input.Dropdown}
                      label="Customer Account Set"
                      labelCols={5 / 12}
                      inputCols={7 / 12}
                      mandatory={!company?.enableWarehouseAccountSets}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="Default Dimension Values" />
                    {dimension1}
                    {dimension2}
                    {dimension3}
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="budgetsTab"
                label="Budgets"
                shouldRender={currentYear}
                permissionReference={'CUSTOMER_BUDGET_PROFILES_VIEW'}
              >
                <BudgetTab type="customer" profileDropdown={CustomerBudgetProfile.dropdown} />
              </Form.TabContent>
              <Form.TabContent id="salesAttributes" label="Sales Defaults">
                <Form.Row>
                  <Form.Fieldset cols={5 / 8}>
                    <Form.Legend label="Defaults" />
                    {renderDefaultPriceBand()}
                    <Form.Field
                      dataid="mandatoryCustomerOrderReference"
                      component={input.Checkbox}
                      label="Order No. Required?"
                    />
                    <Form.Field dataid="warehouse" component={input.Dropdown} label="Warehouse" />
                    <Form.Field dataid="deliveryMethod" component={input.Dropdown} label="Delivery Method" />
                    <Form.Field
                      dataid="defaultCarriageCalculationMethod"
                      component={input.Dropdown}
                      label="Carriage Calculation Method"
                    />
                    <Form.Field dataid="priceSource" component={input.Dropdown} label="Price Source" />
                    <Form.Field dataid="currency" component={input.Dropdown} label="Currency" readOnly={isNotNew} />
                    <Form.Field dataid="paymentMethod" component={input.Dropdown} label="Payment Method" />
                    <Form.Field dataid="certificate" component={input.Dropdown} label="Certificate" />
                    <Form.Field
                      dataid="defaultVatInclusive"
                      component={input.Checkbox}
                      label="VAT Inclusive Prices"
                      reformatLabel={false}
                    />
                    <Form.Field<IDropdownProps>
                      dataid="productSpecialContractPriceOnly"
                      component={input.Dropdown}
                      label="Product Restriction"
                      hint="Determines whether orders and invoices can contain products without special prices or contract prices"
                    />
                    <Form.Field
                      dataid="onlyInvoiceFullyDispatched"
                      component={input.Checkbox}
                      label="Only invoice when fully dispatched"
                    />
                    {showUkims && (
                      <Form.Field<IBlurTextFieldProps>
                        dataid="windsorFrameworkUkims"
                        component={input.BlurTextField}
                        label="UKIMS"
                        reformatLabel={false}
                      />
                    )}
                    <Form.Field<IBlurTextFieldProps>
                      component={input.BlurTextField}
                      dataid="maxoptraDropDurationMinutes"
                      label="MaxOptra Drop Duration (Minutes)"
                      readOnly={false}
                      decimals={0}
                      hint="How long drops take to complete. If set to zero, the value configured in the MaxOptra portal will be used"
                    />
                    <Form.Field<IDropdownProps>
                      dataid="maxoptraOrderPriority"
                      component={input.Dropdown}
                      label="Maxoptra Order Priority"
                    />
                    <Form.Field<ICheckboxInputProps>
                      dataid="tradeCounterDeliverLater"
                      component={input.Checkbox}
                      label="Trade Counter - Deliver Later"
                      hint={
                        "Indicates whether this customer prefers 'deliver later' for trade counter orders - " +
                        'if ticked, the system will prompt for delivery info when paying on trade counter'
                      }
                    />
                    <Form.Legend label="Default Attribute Values" />
                    <Form.Field<IAttributeArrayProps>
                      dataid="defaultSalesAttributeValues"
                      component={input.AttributeArray}
                      label="Sales Attributes"
                      query={SalesAttributeConfig.list}
                      type={'sales'}
                      ignoreTypeForValue={true}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={3 / 8}>
                    <Form.Row>
                      <Form.Fieldset cols={1}>
                        <Form.Legend label="Overall Discount" />
                        {renderOverallDiscountHeader()}
                      </Form.Fieldset>
                    </Form.Row>
                    <Form.Field<IGenericArrayProps>
                      dataid="customerDiscounts"
                      component={input.GenericArray}
                      label="Overall Discount"
                      renderRow={renderOverallDiscount}
                      enableNewRows={true}
                    />
                    <Form.Legend label="Intrastat / Customs / International Trade" />
                    <Form.Field<IDropdownProps>
                      dataid="intrastatDetails.termsOfDelivery"
                      component={input.Dropdown}
                      label="Terms of Delivery"
                      clearable={true}
                      labelCols={2 / 5}
                      inputCols={3 / 5}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="reasonForExport"
                      component={input.BlurTextField}
                      label="Reason for Export"
                      labelCols={2 / 5}
                      inputCols={3 / 5}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="intrastatDetails.natureOfTransactionCode"
                      label="Nature of Transaction"
                      component={input.BlurTextField}
                      hint={natureOfTransactionHint}
                      type={BlurTextFieldType.FluentMask}
                      mask="99"
                      labelCols={2 / 5}
                      inputCols={3 / 5}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="intrastatDetails.customsProcedureCode"
                      label="Customs Procedure Code"
                      component={input.BlurTextField}
                      hint={customsProcedureCodeHint}
                      type={BlurTextFieldType.FluentMask}
                      mask="99-99-999"
                      labelCols={2 / 5}
                      inputCols={3 / 5}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="intrastatDetails.modeOfTransportCode"
                      label="Mode Of Transport Code"
                      component={input.BlurTextField}
                      hint={modeOfTransportHint}
                      type={BlurTextFieldType.FluentMask}
                      mask="9"
                      labelCols={2 / 5}
                      inputCols={3 / 5}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent
                id="attachments"
                label="Attachments"
                badge={attachmentsCount}
                onlyRenderWhenActive={true}
              >
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Main image" />
                    <Form.Field<{ includeLabel: boolean }>
                      dataid="image.fileName"
                      component={input.ImageSelector}
                      includeLabel={false}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Attachments" />
                    <Form.Field<IGridUploaderProps>
                      dataid="attachments"
                      component={input.GridUploader}
                      additionalColumns={[
                        {
                          name: 'Export to Web',
                          key: 'exportToWeb',
                          widthFactor: 1,
                          formatter: Formatters.CheckFormatter,
                          editor: <AnyCheckboxEditor />,
                          editable: true,
                          resizable: true,
                          draggable: false,
                        },
                      ]}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="aliases" label="Aliases">
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Field<any>
                      dataid="alias"
                      component={input.DisplayList}
                      objectKey="reference"
                      label="Customer Alias"
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="documentDefaults" label="Documents">
                <AbstractDocumentConfig
                  forceUpdate={true}
                  documentDefaultList={getDocumentDefaultList()}
                  renderDocumentDetails={renderDocumentDetails}
                />
              </Form.TabContent>
              <Form.TabContent
                id="documents"
                label="Document History"
                tabStyle={{ width: '140px' }}
                shouldRender={customerId !== 'New'}
              >
                <DocumentSummaryWorkspace
                  preview={true}
                  embeddedView={true}
                  predefinedFilters={[
                    {
                      filterOperation: FilterOperation.Equals,
                      filterParameters: {
                        field: 'involves',
                        value: 'CUSTOMER',
                      },
                    },
                    {
                      filterOperation: FilterOperation.Equals,
                      filterParameters: {
                        field: 'companyId',
                        value: props.values.id,
                      },
                    },
                  ]}
                  viewScreenName="/Common/DocumentSummary/Modal"
                  removeAddButton={true}
                />
              </Form.TabContent>
              <Form.TabContent
                id="audit"
                label="Audit"
                key="audit"
                shouldRender={isNotNew}
                permissionReference={'CUSTOMERS_AUDIT_VIEW'}
              >
                <GridContainer>
                  <AuditByFieldWorkspace
                    embeddedView={true}
                    pageView={props.pageView}
                    predefinedFilters={customerIdFilter}
                    viewScreenName="/customers/CustomerAudit/Workspace"
                    entity={CustomerAuditByField}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="priceList"
                label="Price List"
                key="priceList"
                shouldRender={isNotNew}
                permissionReference={'CUSTOMERS_PRICE_LIST_VIEW'}
              >
                <GridContainer>
                  <CustomerPriceListWorkspace
                    embeddedView={true}
                    pageView={props.pageView}
                    predefinedFilters={customerIdFilter}
                    viewScreenName="/customers/CustomerPriceList/Workspace"
                    defaultCustomer={props.values}
                    additionalVariables={{
                      customer: normalizeVariables(props.values),
                    }}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="productHistory"
                label="Data Sheet Product History"
                key="productHistory"
                shouldRender={isNotNew}
                tabStyle={{ width: 160 }}
                permissionReference={'SALES_DOCUMENTS_DATA_SHEETS'}
              >
                <GridContainer>
                  <CustomerProductHistoryWorkspace
                    embeddedView={true}
                    pageView={props.pageView}
                    additionalVariables={{ customer: normalizeVariables(props.values) }}
                    viewScreenName="/customer/CustomerProductHistory/Workspace"
                    contextKey={'/customer/CustomerProductHistory/Workspace'}
                  />
                </GridContainer>
              </Form.TabContent>
              <Form.TabContent
                id="instructionLines"
                label="Delivery Instructions"
                key="instructionLines"
                tabStyle={{ width: 140 }}
              >
                <DeliveryInstructionsTab readOnly={props.readOnly} />
              </Form.TabContent>
              <Form.TabContent id="customer_part_numbers" label="Customer Part Numbers" tabStyle={{ width: 160 }}>
                <Form.Row>
                  <Form.Field<IEditableDataGridProps>
                    addBlankRow={true}
                    dataid="customerPartNumbers"
                    className="entryGrid"
                    enableCellSelect={!getIn(props.values, 'archived')}
                    enableCellAutoFocus={false}
                    columns={defineColumnMeta()}
                    component={input.DataGrid}
                    contentHeight={700}
                    temporaryOnChange={true}
                    temporaryOnFocus={() => true}
                    enableContextMenu={true}
                    deleteOption={deleteCustomerPartNumber}
                  />
                </Form.Row>
              </Form.TabContent>
              <Form.TabContent id="customer_alerts" label="Alerts">
                <Form.Field<IGenericArrayProps>
                  dataid={'formAlerts'}
                  component={input.GenericArray}
                  label="Alerts"
                  renderRow={FormAlertRow}
                  renderDetails={FormAlertDetails}
                  enableNewRows={true}
                  forceUpdate={true}
                  renderRowIsComponent={true}
                  detailProps={{ type: 'Customer' }}
                />
              </Form.TabContent>
            </Form.TabPanel>
          </Form.Form>
        </Form.FormContainer>
      </CurrencyContext.Provider>
    </>
  );
};

const getSubmitValues = ({ values }: { values: ICustomer }) => {
  const { balance, consolidatedBalance, ...valuesWithoutBalance } = values;
  return valuesWithoutBalance;
};

const ConnectedCustomerForm: any = createForm({
  form: type,
  name: formTitle,
  entity: Customer,
  queryFragment: fragment,
  defaultReturnUrl: workspaceUrl,
  getSubmitValues: getSubmitValues,
  shouldComponentUpdate: (props, nextProps) => {
    return props !== nextProps || getIn(props, 'values.contacts') !== getIn(nextProps, 'values.contacts');
  },
})(CustomerForm);

export default ({ temporaryDefault, overrideParams, detailView, ...props }) => {
  const formProps = useSelector((store) => mapStateToFormProps(store, props));
  const formDefault = useFormQuery(
    (temporaryDefault
      ? { default: temporary, instance: instance }
      : { default: defaultQuery, instance: instance }) as EntityType,
    temporaryDefault ? { forceDefault: true, idParam: 'uuid', overrideParams } : { duplicate: true, overrideParams }
  );
  const cacheUpdate = useEntityCacheUpdate('customers', 'customer', { detailView });
  const [formUpdate] = useMutation(update as DocumentNode, {
    update: cacheUpdate,
    refetchQueries: [CustomerNotesWorkspaceQuery],
  });
  const [formTemporaryUpdate] = useMutation(temporaryUpdate as DocumentNode);
  const dispatch = useDispatch();
  const app: any = useContext(AppContext);
  const history = useHistory<{ activeTabId?: string }>();

  const customerConfig = useCustomerConfig();
  const companyUser = app?.fullUserDetails?.companyUser;

  return (
    <ConnectedCustomerForm
      {...formProps}
      {...props}
      formUpdate={formUpdate}
      formTemporaryUpdate={formTemporaryUpdate}
      formData={formDefault}
      dispatch={dispatch}
      app={app}
      companyUser={companyUser}
      customerConfig={customerConfig}
      overrideParams={overrideParams}
      activeTabId={props.activeTabId || history.location?.state?.activeTabId}
    />
  );
};
